import moment from 'moment';
import { message } from 'antd';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePublishFormMutation, useSaveAsDraftFormMutation } from '../../../../../../api/expenseAreas';
import {
    FormItem,
    OptionI,
    Section,
    SettingI,
    SupplierProfileQuestionaryComponentsI,
    SupplierProfileQuestionaryFormData
} from '../../../../../../api/types/expenseareas';
import {
    normalizeSpqBackendValidationErrors,
    parseApiValidationPayload
} from './spqBackendValidation';

export type SpqPublishResult =
    | { ok: true }
    | { ok: false; reason: 'no-sections' }
    | { ok: false; reason: 'invalid'; sectionErrors: string[]; fieldsErrors: string[] };

function collectSpqValidationErrors(sections: Section[] | undefined) {
    const sectionErrors: string[] = [];
    const fieldsErrors: string[] = [];

    sections?.forEach((section) => {
        if (!section.title || !section.fields?.length) {
            sectionErrors.push(String(section?.localId));
        }

        section?.fields.forEach((field) => {
            const type = field.type;
            let validField = true;
            if (type === 'short-answer' || type === 'paragraph' || type === 'explanation-banner') {
                if (!field.label) {
                    fieldsErrors.push(String(field?.id));
                }
            }
            if (type === 'single-select' || type === 'multiple-choice' || type === 'dropdown') {
                if (!field.label) {
                    validField = false;
                } else if (!field?.options?.every((option) => option.value)) {
                    validField = false;
                } else if (
                    field.settings.find((setting) => setting.type === 'rating-points')?.switchValue &&
                    !field.settings
                        .find((setting) => setting.type === 'question_category')
                        ?.options?.some((option) => option.selected)
                ) {
                    validField = false;
                }

                if (!validField) {
                    fieldsErrors.push(String(field?.id));
                }
            }
        });
    });

    return { sectionErrors, fieldsErrors };
}

function mapFormDataToApiBody(data: SupplierProfileQuestionaryFormData) {
    return {
        ...data,
        sections: data?.sections.map((section, index) => ({
            ...section,
            order: index,
            id: typeof section?.id === 'string' ? null : section?.id,
            fields: section.fields?.map((field, index) => ({
                ...field,
                order: index,
                id: typeof field?.id === 'string' ? null : field?.id,

                options: field?.options?.map((option) => {
                    const { go_to_next_section_local, ...restOption } = option;
                    return {
                        ...restOption,
                        label: option.value,
                        go_to_next_section: field?.settings?.find(
                            (setting) => setting?.type === 'go_to_section'
                        )?.switchValue
                            ? (() => {
                                  const index = option?.go_to_next_section_local
                                      ? data?.sections?.findIndex(
                                            (section) =>
                                                section?.localId + '' === option?.go_to_next_section_local + ''
                                        )
                                      : data?.sections?.findIndex(
                                            (section) =>
                                                section?.order + '' === option?.go_to_next_section + ''
                                        );
                                  return index !== -1 ? index : null;
                              })()
                            : null
                    };
                })
            }))
        }))
    };
}

export default function useSupplierProfileQuestionaryController() {
    const location = useLocation();
    const ExpenseAreaId = useMemo(() => location.pathname.split('/')[2], [location.pathname]);

    const [isCantPublishModal, setIsCantPublishModal] = useState(false);
    const [sectionsError, setSectionsError] = useState<string[]>([]);
    const [fieldsError, setFieldsError] = useState<string[]>([]);
    const [backendSpqErrors, setBackendSpqErrors] = useState<Record<string, string[]>>({});
    const [fields, setFields] = useState<any>([]);
    const [selectedElement, setSelectedElement] = useState<null | string>(null);
    const [componentnsData, setComponentsData] = useState<SupplierProfileQuestionaryComponentsI | undefined>();

    const baseOptionData = componentnsData?.data?.field_option;

    const [data, setData] = useState<SupplierProfileQuestionaryFormData>({
        title: '',
        id: null,
        expense_area_id: null,
        type: '',
        status: '',
        description: '',
        user_id: null,
        sections: []
    });

    const [publishForm, { isSuccess: isSuccessPublishForm, isLoading: isLoadingPublished }] = usePublishFormMutation();

    const [saveAsDraft, { isSuccess: isSuccessSaveAsDraft, isLoading: isLoadingDraft }] = useSaveAsDraftFormMutation();

    const publish = (): SpqPublishResult => {
        if (!data?.sections?.length) {
            setIsCantPublishModal(true);
            return { ok: false, reason: 'no-sections' };
        }
        const { sectionErrors, fieldsErrors: sectionFieldsErrors } = collectSpqValidationErrors(data?.sections);
        setSectionsError(sectionErrors);
        setFieldsError(sectionFieldsErrors);

        if (sectionErrors?.length || sectionFieldsErrors?.length) {
            setBackendSpqErrors({});
            return {
                ok: false,
                reason: 'invalid',
                sectionErrors,
                fieldsErrors: sectionFieldsErrors
            };
        }

        const baseData = {
            expense_area_id: ExpenseAreaId,
            type: 'supplier-profile-questionnaire',
            data: mapFormDataToApiBody(data)
        };

        publishForm(baseData)
            .unwrap()
            .then(() => setBackendSpqErrors({}))
            .catch((err: unknown) => {
                const { rawErrors, message: apiMessage } = parseApiValidationPayload(err);
                setBackendSpqErrors(
                    normalizeSpqBackendValidationErrors(rawErrors, data?.sections ?? [])
                );
                if (apiMessage) {
                    message.error(apiMessage);
                }
            });

        return { ok: true };
    };

    const saveAsDraftFunc = () => {
        const baseData = {
            expense_area_id: ExpenseAreaId,
            type: 'supplier-profile-questionnaire',
            data: mapFormDataToApiBody(data)
        };

        saveAsDraft(baseData)
            .unwrap()
            .then(() => setBackendSpqErrors({}))
            .catch((err: unknown) => {
                const { rawErrors, message: apiMessage } = parseApiValidationPayload(err);
                setBackendSpqErrors(
                    normalizeSpqBackendValidationErrors(rawErrors, data?.sections ?? [])
                );
                if (apiMessage) {
                    message.error(apiMessage);
                }
            });
    };

    const addNewSection = () => {
        const timestamp = moment().format('YYYYMMDDHHmmssSSS');

        const ID = `section-${timestamp}`;

        const newSection: Section = {
            id: ID,
            title: '',
            description: '',
            fields: [],
            localId: timestamp,
            order: data?.sections?.length
        };
        setData((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                sections: [...prev.sections, newSection]
            };
        });
        setSelectedElement(ID);
    };

    function updateFieldOrSection(fieldName: keyof Section | keyof FormItem, newValue: string) {
        if (selectedElement) {
            setBackendSpqErrors((prev) => {
                const prefix = `field:${selectedElement}:`;
                const next = { ...prev };
                for (const k of Object.keys(next)) {
                    if (k.startsWith(prefix)) delete next[k];
                }
                return next;
            });
        }
        setData((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                sections: prev.sections.map((section) => {
                    const itemIndex = section.fields.findIndex((item) => item.id + '' === selectedElement);
                    if (itemIndex === -1) return section;

                    const updatedItems = [...section.fields];
                    updatedItems[itemIndex] = {
                        ...updatedItems[itemIndex],
                        [fieldName]: newValue
                    };
                    return { ...section, fields: updatedItems };
                })
            };
        });
    }

    function updateSettingField(fieldName: keyof SettingI, newValue: boolean, settingType: string) {
        if (selectedElement) {
            setBackendSpqErrors((prev) => {
                const sid = String(selectedElement);
                const next = { ...prev };
                for (const k of Object.keys(next)) {
                    if (
                        k.startsWith(`option:${sid}:`) ||
                        k.startsWith(`field:${sid}:`)
                    ) {
                        delete next[k];
                    }
                }
                return next;
            });
        }
        setData((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                sections: prev.sections.map((section) => {
                    const itemIndex = section.fields.findIndex((item) => item.id + '' === selectedElement);
                    if (itemIndex === -1) return section;

                    const updatedItems = [...section.fields];
                    const targetItem = updatedItems[itemIndex];

                    updatedItems[itemIndex] = {
                        ...targetItem,
                        settings: targetItem.settings.map((s) =>
                            s.type === settingType ? { ...s, [fieldName]: newValue } : s
                        )
                    };

                    return { ...section, fields: updatedItems };
                })
            };
        });
    }

    function updateOptionField(optionIndex: number, fieldName: keyof OptionI, newValue: string | boolean | number) {
        if (selectedElement) {
            setBackendSpqErrors((prev) => {
                const prefix = `option:${selectedElement}:${optionIndex}:`;
                const next = { ...prev };
                for (const k of Object.keys(next)) {
                    if (k.startsWith(prefix)) delete next[k];
                }
                return next;
            });
        }
        setData((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                sections: prev.sections.map((section) => {
                    const itemIndex = section.fields.findIndex((item) => item.id + '' === selectedElement);
                    if (itemIndex === -1) return section;

                    const updatedItems = [...section.fields];
                    const targetItem = updatedItems[itemIndex];

                    if (!targetItem.options || optionIndex < 0 || optionIndex >= targetItem.options.length) {
                        return section;
                    }

                    const updatedOptions = [...targetItem.options];
                    updatedOptions[optionIndex] = {
                        ...updatedOptions[optionIndex],
                        [fieldName]: newValue
                    };

                    updatedItems[itemIndex] = {
                        ...targetItem,
                        options: updatedOptions
                    };

                    return {
                        ...section,
                        fields: updatedItems
                    };
                })
            };
        });
    }

    function addOption() {
        setData((prev) => {
            if (!prev || !baseOptionData) return prev;

            return {
                ...prev,
                sections: prev.sections.map((section) => {
                    const itemIndex = section.fields.findIndex((item) => item.id + '' === selectedElement);
                    if (itemIndex === -1) return section;

                    const updatedItems = [...section.fields];
                    const targetItem = updatedItems[itemIndex];

                    const updatedOptions = targetItem.options ? [...targetItem.options] : [];

                    updatedOptions.push(baseOptionData);

                    updatedItems[itemIndex] = {
                        ...targetItem,
                        options: updatedOptions
                    };

                    return {
                        ...section,
                        fields: updatedItems
                    };
                })
            };
        });
    }

    function updateRadioOptionSelectedInSetting(settingType: string, selectedOptionIndex: number) {
        setData((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                sections: prev.sections.map((section) => {
                    const itemIndex = section.fields.findIndex((item) => item.id + '' === selectedElement);
                    if (itemIndex === -1) return section;

                    const updatedItems = [...section.fields];
                    const targetItem = updatedItems[itemIndex];

                    updatedItems[itemIndex] = {
                        ...targetItem,
                        settings: targetItem.settings.map((s) => {
                            if (s.type !== settingType) return s;

                            if (!s.options || s.options.length !== 2) {
                                return s; // ожидаем ровно 2 опции
                            }

                            const updatedOptions = s.options.map((option, idx) => ({
                                ...option,
                                selected: idx === selectedOptionIndex
                            }));

                            return {
                                ...s,
                                options: updatedOptions
                            };
                        })
                    };

                    return {
                        ...section,
                        fields: updatedItems
                    };
                })
            };
        });
    }

    function updateOptionSelectedInSetting(settingType: string, optionID: string) {
        setData((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                sections: prev.sections.map((section) => {
                    const itemIndex = section.fields.findIndex((item) => item.id + '' === selectedElement);
                    if (itemIndex === -1) return section;

                    const updatedItems = [...section.fields];
                    const targetItem = updatedItems[itemIndex];

                    updatedItems[itemIndex] = {
                        ...targetItem,
                        settings: targetItem.settings.map((s) => {
                            if (s.type !== settingType) return s;

                            const updatedOptions = s.options ? [...s.options] : [];
                            const optionIndex = updatedOptions.findIndex((opt) => opt.value === optionID);

                            if (optionIndex === -1) {
                                return s;
                            }

                            updatedOptions[optionIndex] = {
                                ...updatedOptions[optionIndex],
                                selected: !updatedOptions[optionIndex].selected
                            };

                            return {
                                ...s,
                                options: updatedOptions
                            };
                        })
                    };

                    return {
                        ...section,
                        fields: updatedItems
                    };
                })
            };
        });
    }

    function selectAllOptions(settingType: string) {
        setData((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                sections: prev.sections.map((section) => {
                    const itemIndex = section.fields.findIndex((item) => item.id + '' === selectedElement);
                    if (itemIndex === -1) return section;

                    const updatedItems = [...section.fields];
                    const targetItem = updatedItems[itemIndex];

                    updatedItems[itemIndex] = {
                        ...targetItem,
                        settings: targetItem.settings.map((s) => {
                            if (s.type !== settingType) return s;

                            const updatedOptions = s.options
                                ? s.options.map((opt) => ({ ...opt, selected: true }))
                                : [];

                            return {
                                ...s,
                                options: updatedOptions
                            };
                        })
                    };

                    return {
                        ...section,
                        fields: updatedItems
                    };
                })
            };
        });
    }

    function deselectAllOptions(settingType: string) {
        setData((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                sections: prev.sections.map((section) => {
                    const itemIndex = section.fields.findIndex((item) => item.id + '' === selectedElement);
                    if (itemIndex === -1) return section;

                    const updatedItems = [...section.fields];
                    const targetItem = updatedItems[itemIndex];

                    updatedItems[itemIndex] = {
                        ...targetItem,
                        settings: targetItem.settings.map((s) => {
                            if (s.type !== settingType) return s;

                            const updatedOptions = s.options
                                ? s.options.map((opt) => ({ ...opt, selected: false }))
                                : [];

                            return {
                                ...s,
                                options: updatedOptions
                            };
                        })
                    };

                    return {
                        ...section,
                        fields: updatedItems
                    };
                })
            };
        });
    }

    function removeOption(optionIndex: number) {
        if (selectedElement) {
            setBackendSpqErrors((prev) => {
                const sid = String(selectedElement);
                const next = { ...prev };
                for (const k of Object.keys(next)) {
                    if (k.startsWith(`option:${sid}:`)) delete next[k];
                }
                return next;
            });
        }
        setData((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                sections: prev.sections.map((section) => {
                    const itemIndex = section.fields.findIndex((item) => item.id + '' === selectedElement);
                    if (itemIndex === -1) return section;

                    const updatedItems = [...section.fields];
                    const targetItem = updatedItems[itemIndex];

                    const updatedOptions = targetItem.options
                        ? targetItem.options.filter((_, idx) => idx !== optionIndex)
                        : [];

                    updatedItems[itemIndex] = {
                        ...targetItem,
                        options: updatedOptions
                    };

                    return {
                        ...section,
                        fields: updatedItems
                    };
                })
            };
        });
    }

    function removeItem() {
        const id = selectedElement;
        if (id) {
            setBackendSpqErrors((prev) => {
                const sid = String(id);
                const next = { ...prev };
                for (const k of Object.keys(next)) {
                    if (k.startsWith(`option:${sid}:`) || k.startsWith(`field:${sid}:`)) {
                        delete next[k];
                    }
                }
                return next;
            });
        }
        setData((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                sections: prev.sections.map((section) => {
                    return {
                        ...section,
                        fields: section.fields.filter((item) => item.id + '' !== selectedElement + '')
                    };
                })
            };
        });
    }

    function removeSection() {
        const sel = selectedElement;
        setBackendSpqErrors((prev) => {
            const sec = data?.sections?.find((s) => s.id === sel);
            if (!sec) return prev;
            const next = { ...prev };
            const fieldIdSet = new Set((sec.fields ?? []).map((f) => String(f.id)));
            for (const k of Object.keys(next)) {
                if (k.startsWith(`section:${String(sec.localId)}:`)) {
                    delete next[k];
                    continue;
                }
                const om = k.match(/^option:([^:]+):/);
                const fm = k.match(/^field:([^:]+):/);
                const fid = om?.[1] ?? fm?.[1];
                if (fid && fieldIdSet.has(fid)) delete next[k];
            }
            return next;
        });
        setData((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                sections: prev.sections.filter((section) => section.id !== selectedElement)
            };
        });
    }

    const findItem = (): FormItem | null => {
        for (const section of data?.sections) {
            const found = section?.fields?.find((item) => item.id + '' === selectedElement);
            if (found) return found;
        }

        return null;
    };

    const clearBackendSpqErrorsForSectionLocalId = (sectionLocalId: string | number) => {
        setBackendSpqErrors((prev) => {
            const prefix = `section:${String(sectionLocalId)}:`;
            const next = { ...prev };
            for (const k of Object.keys(next)) {
                if (k.startsWith(prefix)) delete next[k];
            }
            return next;
        });
    };

    document.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        if (
            target.classList.contains('main__form') ||
            target.classList.contains('builder__main') ||
            target.classList.contains('builder__header')
        ) {
            setSelectedElement(null);
        }
    });

    const maxSwitchValue =
        findItem()?.type === 'single-select'
            ? Math.max(0, ...(findItem()?.options?.map((elem) => Number(elem.rating) || 0) || []))
            : findItem()?.options?.reduce((sum, elem) => sum + (Number(elem.rating) || 0), 0) || 0;

    const moveSectionToStart = (localId: string) => {
        if (!data?.sections?.length) return;

        setData((prev) => {
            const arr = structuredClone(prev.sections) || [];
            const index = arr.findIndex((el: Section) => el.localId === localId);

            if (index === -1) return prev;

            const [item] = arr.splice(index, 1);
            return {
                ...prev,
                sections: [item, ...arr]
            };
        });
    };

    const moveSectionToEnd = (localId: string) => {
        if (!data?.sections?.length) return;

        setData((prev) => {
            const arr = structuredClone(prev.sections) || [];
            const index = arr.findIndex((el: Section) => el.localId === localId);

            if (index === -1) return prev; // элемент не найден, ничего не делаем

            const [item] = arr.splice(index, 1); // удаляем элемент из массива
            return {
                ...prev,
                sections: [...arr, item] // добавляем в конец
            };
        });
    };

    const moveSectionDown = (localId: string) => {
        if (!data?.sections?.length) return;

        setData((prev) => {
            const arr = structuredClone(prev.sections) || [];
            const index = arr.findIndex((el: Section) => el.localId === localId);

            if (index === -1 || index === arr.length - 1) return prev; // элемент не найден или уже последний

            // меняем местами с следующим элементом
            [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];

            return {
                ...prev,
                sections: arr
            };
        });
    };

    const moveSectionUp = (localId: string) => {
        if (!data?.sections?.length) return;

        setData((prev) => {
            const arr = structuredClone(prev.sections) || [];
            const index = arr.findIndex((el: Section) => el.localId === localId);

            if (index <= 0) return prev; // элемент не найден или уже первый

            // меняем местами с предыдущим элементом
            [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];

            return {
                ...prev,
                sections: arr
            };
        });
    };

    return {
        publish,
        saveAsDraftFunc,
        isSuccessSaveAsDraft,
        isLoadingDraft,
        data,
        setData,
        isCantPublishModal,
        setIsCantPublishModal,
        sectionsError,
        setSectionsError,
        fieldsError,
        setFieldsError,
        backendSpqErrors,
        setBackendSpqErrors,
        clearBackendSpqErrorsForSectionLocalId,
        isSuccessPublishForm,
        ExpenseAreaId,
        isLoadingPublished,
        setFields,
        fields,
        addNewSection,
        selectedElement,
        setSelectedElement,
        updateFieldOrSection,
        updateSettingField,
        updateOptionField,
        addOption,
        updateRadioOptionSelectedInSetting,
        updateOptionSelectedInSetting,
        selectAllOptions,
        deselectAllOptions,
        removeOption,
        removeItem,
        removeSection,
        findItem,
        maxSwitchValue,
        moveSectionToStart,
        moveSectionToEnd,
        moveSectionDown,
        moveSectionUp,
        setComponentsData
    };
}
